import { useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { relatorioTalhaoVisitaUrl, useTalhao, useTalhaoRelatorios } from '../api';
import type { RelatorioListItem } from '../schemas';
import { MANEJO_CATS } from '../manejo-cats';
import { bboxOf } from '../visita/staticmap';
import { OfflineMap } from './OfflineMap';
import { SeverityChip } from './SeverityChip';

function fmtHa(ha: number | null | undefined): string {
  if (ha == null) return '—';
  return `${ha.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} ha`;
}
function fmtData(s: string): { dia: string; mes: string } {
  try {
    const d = new Date(s);
    return { dia: String(d.getDate()).padStart(2, '0'), mes: d.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '').toUpperCase() };
  } catch {
    return { dia: '--', mes: '' };
  }
}

// Tela 05 · Talhão (detalhe) — manejo + histórico de visitas (mobile).
// Tela 18 · Talhão (desktop/web): ≥lg coluna esquerda 380px (título · ha,
// manejo, histórico) e mapa do talhão preenchendo a direita.
export function TalhaoDetailView({ talhaoId }: { talhaoId: number }) {
  const navigate = useNavigate();
  const { data: t } = useTalhao(talhaoId);
  const { data: relatorios, isLoading } = useTalhaoRelatorios(talhaoId);

  // Bounds do próprio polígono do talhão (ST_AsGeoJSON) → mapa abre enquadrado NELE,
  // sem depender de casar id no bundle. Vale online e offline (talhão vem cacheado).
  const focusBounds = useMemo<[[number, number], [number, number]] | undefined>(() => {
    const g = t?.geojson;
    if (!g) return undefined;
    const bb = bboxOf([g]);
    return bb ? [[bb.minLat, bb.minLon], [bb.maxLat, bb.maxLon]] : undefined;
  }, [t?.geojson]);

  const titulo = t?.talhao_nome || (t ? `Talhão ${t.numero}` : 'Talhão');
  const sub = [t?.fazenda, fmtHa(t?.area_ha)].filter(Boolean).join(' · ');
  const linha = [t?.cultura_atual, t?.variedade_atual].filter(Boolean).join(' · ');
  // Severidade do título (desktop): última visita com alertas → atenção.
  const ultima = (relatorios ?? [])[0];
  const sevTitulo = ultima && ultima.qtd_alertas > 0 ? 'atencao' : 'ok';

  return (
    <div className="relative flex h-full flex-col bg-bg">
      {/* header mobile — no desktop o título vive na coluna esquerda */}
      <header className="flex shrink-0 flex-col px-3 pt-4 pb-3 lg:hidden">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => window.history.back()}
            className="-ml-1 flex size-9 shrink-0 items-center justify-center rounded-full text-text hover:bg-surface2"
            aria-label="Voltar"
          >
            <ChevronLeft size={26} strokeWidth={2} />
          </button>
          <h1 className="min-w-0 flex-1 truncate text-[1.35rem] font-bold leading-tight tracking-tight text-text">{titulo}</h1>
        </div>
        <span className="pl-9 text-[0.85rem] text-text-2">{sub || '—'}</span>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto pb-40 lg:overflow-hidden lg:pb-0">
        <div className="lg:grid lg:h-full lg:grid-cols-[380px_minmax(0,1fr)] lg:gap-6 lg:px-8 lg:pb-8 lg:pt-7">
          {/* mapa do talhão — mobile: bloco no topo; desktop: coluna direita inteira */}
          <div className="mx-4 mb-4 h-[473px] overflow-hidden rounded-2xl bg-surface2 lg:order-2 lg:m-0 lg:h-full lg:min-h-0 lg:rounded-[14px]">
            {t?.imovel_id != null ? (
              <OfflineMap imovelId={t.imovel_id} focusTalhaoId={talhaoId} focusBounds={focusBounds} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-text-muted">Carregando mapa…</div>
            )}
          </div>

          {/* coluna esquerda (desktop) / restante da página (mobile) */}
          <div className="scroll-auto-hide flex flex-col lg:order-1 lg:h-full lg:min-h-0 lg:gap-[18px] lg:overflow-y-auto lg:pr-2.5">
            {/* título — desktop: nome · ha + severidade + contexto */}
            <div className="hidden lg:order-first lg:block">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => window.history.back()}
                  className="flex size-8 shrink-0 items-center justify-center rounded-full text-text-2 hover:bg-surface2 hover:text-text"
                  aria-label="Voltar"
                >
                  <ChevronLeft size={22} strokeWidth={2} />
                </button>
                <h1 className="min-w-0 flex-1 truncate text-xl font-semibold leading-7 text-text">
                  {titulo} · {fmtHa(t?.area_ha)}
                </h1>
                <SeverityChip severity={sevTitulo} />
              </div>
              <span className="pl-[38px] text-sm text-text-2">
                {[t?.fazenda, linha].filter(Boolean).join(' · ') || '—'}
              </span>
            </div>

            {/* cultura em destaque — só mobile (no desktop está no subtítulo) */}
            {linha && <p className="order-1 px-4 pb-1 text-[0.95rem] font-semibold text-text lg:hidden">{linha}</p>}

            {/* manejo — grid de dados + categorias */}
            <div className="order-2 lg:order-none">
              <p className="px-5 pb-2 pt-3 text-[0.72rem] font-semibold uppercase tracking-wider text-accent lg:px-0 lg:pt-0 lg:text-[11px] lg:font-medium lg:tracking-[0.5px]">
                Manejo
              </p>
              <div className="mx-4 mb-2.5 grid grid-cols-2 gap-x-4 gap-y-3 rounded-[14px] border border-[var(--agro-card-border)] bg-surface2 px-4 py-4 lg:mx-0 lg:mb-[18px]">
                <Campo label="Cultura" valor={t?.cultura_atual} />
                <Campo label="Cultivar" valor={t?.variedade_atual} />
                <Campo label="Safra" valor={t?.safra_atual} />
                <Campo label="Área" valor={fmtHa(t?.area_ha)} />
              </div>
              <div className="mx-4 overflow-hidden rounded-[14px] border border-[var(--agro-card-border)] bg-surface2 lg:mx-0">
                {MANEJO_CATS.map((m, i) => (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => void navigate({ to: '/agronomia/manejo/$id/$cat', params: { id: String(talhaoId), cat: m.key } })}
                    className={`flex w-full items-center gap-3 px-4 py-3.5 text-left hover:bg-[var(--nav-hover-bg)] ${i > 0 ? 'border-t border-border' : ''}`}
                  >
                    <m.icon size={20} strokeWidth={1.75} className="shrink-0 text-accent" />
                    <span className="flex-1 text-[0.92rem] text-text">{m.label}</span>
                    <ChevronRight size={18} strokeWidth={2} className="text-[#565350]" />
                  </button>
                ))}
              </div>
            </div>

            {/* histórico de visitas */}
            <div className="order-3 lg:order-none">
              <p className="px-5 pb-2 pt-5 text-[0.72rem] font-semibold uppercase tracking-wider text-accent lg:px-0 lg:pt-0 lg:text-[11px] lg:font-medium lg:tracking-[0.5px]">
                Histórico de visitas
              </p>
              {isLoading && <p className="py-6 text-center text-sm text-text-muted">Carregando…</p>}
              {!isLoading && (relatorios ?? []).length === 0 && (
                <p className="py-8 text-center text-sm text-text-muted">Nenhuma visita registrada.</p>
              )}
              <div className="flex flex-col gap-2.5 px-4 lg:gap-2 lg:px-0">
                {(relatorios ?? []).map((r) => (
                  <VisitaRow
                    key={r.id}
                    r={r}
                    // Histórico abre o RELATÓRIO no MESMO layout do consolidado,
                    // filtrado à visita e ao talhão em questão.
                    onClick={
                      r.visita_campo_id != null && t?.imovel_id != null
                        ? () =>
                            window.open(
                              relatorioTalhaoVisitaUrl(t.imovel_id!, r.visita_campo_id!, talhaoId),
                              '_blank',
                            )
                        : undefined
                    }
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => t?.imovel_id && void navigate({ to: '/agronomia/fazendas/$id/visita', params: { id: String(t.imovel_id) } })}
        className="absolute bottom-24 right-4 z-10 inline-flex items-center gap-2 rounded-full bg-[var(--accent)] px-5 py-3.5 text-[0.92rem] font-semibold text-[var(--on-solid)] shadow-[var(--shadow-over)] lg:hidden"
      >
        <Plus size={20} strokeWidth={2.25} /> Nova visita técnica
      </button>
    </div>
  );
}

function Campo({ label, valor }: { label: string; valor: string | null | undefined }) {
  return (
    <div className="flex flex-col">
      <span className="text-[0.72rem] text-text-muted">{label}</span>
      <span className="text-[0.9rem] font-medium text-text">{valor || '—'}</span>
    </div>
  );
}

function VisitaRow({ r, onClick }: { r: RelatorioListItem; onClick?: (() => void) | undefined }) {
  const { dia, mes } = fmtData(r.data_visita);
  const atencao = r.qtd_alertas > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className="flex w-full items-center gap-3 rounded-[12px] border border-[var(--agro-card-border)] bg-surface2 px-3 py-2.5 text-left active:scale-[0.99] disabled:active:scale-100"
    >
      <span className="flex size-12 shrink-0 flex-col items-center justify-center rounded-[11px] bg-surface3 lg:size-11">
        <span className="text-[0.95rem] font-bold leading-none text-text">{dia}</span>
        <span className="text-[0.6rem] font-semibold uppercase text-text-muted">{mes}</span>
      </span>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="line-clamp-2 text-[0.88rem] font-medium leading-tight text-text">
          {r.comentario || 'Visita técnica'}
        </span>
        <span className="text-[0.78rem] text-text-2">
          {[r.agronomo_nome || 'Agrônomo', `${r.fotos} ${r.fotos === 1 ? 'foto' : 'fotos'}`].join(' · ')}
        </span>
      </div>
      <SeverityChip severity={atencao ? 'atencao' : 'ok'} />
      <ChevronRight size={16} strokeWidth={2} className="shrink-0 text-[#565350]" />
    </button>
  );
}
